import { useCallback, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { OrganiseDocumentStatus, OrganiseDocumentTag } from '@karehero/models';
import {
  formatFileSize,
  ACCEPTED_DOCUMENT_TYPES,
  ACCEPTED_DOCUMENT_TYPES_LABEL,
  MAX_DOCUMENT_SIZE_MB,
  MAX_DOCUMENT_SIZE_BYTES,
  isAcceptedDocumentType,
} from '@karehero/helpers/files';
import { formatDate } from '@karehero/helpers/datetime';

import { Icon } from '@/components/atoms/Icon/Icon';

export interface AdminDocumentItem {
  id: string;
  title: string;
  filename: string;
  sizeBytes: number;
  status: OrganiseDocumentStatus;
  tags?: OrganiseDocumentTag[];
  createdAt: string;
  uploaderName?: string;
}

export interface DocumentUploadData {
  title: string;
  description?: string;
  file: File;
  tags: string[];
}

export interface AdminDocumentsPanelProps {
  documents: AdminDocumentItem[];
  availableTags: OrganiseDocumentTag[];
  isLoading?: boolean;
  isUploading?: boolean;
  onUpload: (data: DocumentUploadData) => void;
  onDownload: (doc: AdminDocumentItem) => void;
  onDelete: (doc: AdminDocumentItem) => void;
}

const DocumentRow = ({
  doc,
  onDownload,
  onDelete,
}: {
  doc: AdminDocumentItem;
  onDownload: (doc: AdminDocumentItem) => void;
  onDelete: (doc: AdminDocumentItem) => void;
}) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  return (
    <Stack
      direction='row'
      spacing={1.5}
      sx={{
        alignItems: 'center',
        py: 1.5,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Icon
        icon='file-contract'
        fontSize={24}
        color={theme.palette.text.secondary}
      />
      <Stack
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography
          variant='body1'
          noWrap
          sx={{
            fontWeight: 600,
          }}
        >
          {doc.title}
        </Typography>
        <Typography
          variant='caption'
          sx={{
            color: 'text.secondary',
          }}
        >
          {formatFileSize(doc.sizeBytes)}
          {doc.uploaderName && ` · Uploaded by ${doc.uploaderName}`}
          {` · ${formatDate(doc.createdAt, { omitDay: true })}`}
        </Typography>
      </Stack>

      {doc.status === OrganiseDocumentStatus.Scanning && (
        <CircularProgress size={20} />
      )}

      {doc.tags && doc.tags.length > 0 && (
        <Stack direction='row' spacing={0.5}>
          {doc.tags.map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              size='small'
              variant='outlined'
            />
          ))}
        </Stack>
      )}

      <IconButton
        size='small'
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        aria-label='Document actions'
      >
        <Icon icon='ellipsis-vertical' fontSize={16} />
      </IconButton>

      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        {doc.status === OrganiseDocumentStatus.Scanned && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onDownload(doc);
            }}
          >
            <ListItemIcon>
              <Icon icon='download' fontSize={16} />
            </ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onDelete(doc);
          }}
        >
          <ListItemIcon sx={{ color: 'error.main' }}>
            <Icon icon='trash' fontSize={16} />
          </ListItemIcon>
          <ListItemText slotProps={{ primary: { color: 'error' } }}>
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>
    </Stack>
  );
};

const UploadDialog = ({
  isOpen,
  onClose,
  file,
  availableTags,
  isUploading,
  onUpload,
}: {
  isOpen: boolean;
  onClose: () => void;
  file: File | null;
  availableTags: OrganiseDocumentTag[];
  isUploading?: boolean;
  onUpload: (data: DocumentUploadData) => void;
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const handleTagChange = useCallback((event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedTags(typeof value === 'string' ? value.split(',') : value);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!file || !title.trim()) return;
    onUpload({
      title: title.trim(),
      description: description.trim() || undefined,
      file,
      tags: selectedTags,
    });
  }, [file, title, description, selectedTags, onUpload]);

  const handleClose = useCallback(() => {
    if (isUploading) return;
    setTitle('');
    setDescription('');
    setSelectedTags([]);
    onClose();
  }, [isUploading, onClose]);

  const lastFileRef = useRef<File | null>(null);
  if (file && file !== lastFileRef.current) {
    lastFileRef.current = file;
    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
    setTitle(nameWithoutExt);
    setDescription('');
    setSelectedTags([]);
  }

  const canSubmit = !!title.trim() && !!file && !isUploading;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Upload document</DialogTitle>
      <DialogContent>
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
            mb: 3,
          }}
        >
          {ACCEPTED_DOCUMENT_TYPES_LABEL}, up to {MAX_DOCUMENT_SIZE_MB}MB.
          Everyone in the care circle will be able to see it.
        </Typography>
        <Stack spacing={2.5}>
          <TextField
            label='Document name'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            disabled={isUploading}
            autoFocus
          />
          <Select
            multiple
            value={selectedTags}
            onChange={handleTagChange}
            displayEmpty
            disabled={isUploading}
            fullWidth
            renderValue={(selected) => {
              if (selected.length === 0) return 'Tags';
              return selected
                .map((id) => availableTags.find((t) => t.id === id)?.name || id)
                .join(', ');
            }}
          >
            {availableTags.map((tag) => (
              <MenuItem key={tag.id} value={tag.id}>
                {tag.name}
              </MenuItem>
            ))}
          </Select>
          <TextField
            label='Add a note (optional)'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
            disabled={isUploading}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={handleClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={
            isUploading ? <CircularProgress size={16} color='inherit' /> : null
          }
        >
          {isUploading ? 'Uploading…' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const AdminDocumentsPanel = ({
  documents,
  availableTags,
  isLoading,
  isUploading,
  onUpload,
  onDownload,
  onDelete,
}: AdminDocumentsPanelProps) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const filteredDocuments = useMemo(() => {
    let filtered = documents.filter(
      (d) => d.status !== OrganiseDocumentStatus.Pending,
    );
    if (search) {
      const lower = search.toLowerCase();
      filtered = filtered.filter(
        (d) =>
          d.title.toLowerCase().includes(lower) ||
          d.filename.toLowerCase().includes(lower),
      );
    }
    if (tagFilter) {
      filtered = filtered.filter((d) =>
        d.tags?.some((t) => t.id === tagFilter),
      );
    }
    return filtered;
  }, [documents, search, tagFilter]);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (file.size > MAX_DOCUMENT_SIZE_BYTES) return;
      if (!isAcceptedDocumentType(file.name)) return;

      setPendingFile(file);
      setIsDialogOpen(true);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [],
  );

  const handleUpload = useCallback(
    (data: DocumentUploadData) => {
      onUpload(data);
      setIsDialogOpen(false);
      setPendingFile(null);
    },
    [onUpload],
  );

  const handleDialogClose = useCallback(() => {
    setIsDialogOpen(false);
    setPendingFile(null);
  }, []);

  return (
    <Stack
      sx={{
        height: '100%',
      }}
    >
      <input
        ref={fileInputRef}
        type='file'
        accept={ACCEPTED_DOCUMENT_TYPES.join(',')}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <Stack
        direction='row'
        spacing={1.5}
        sx={{
          alignItems: 'center',
          mb: 2,
        }}
      >
        <TextField
          size='small'
          placeholder='Search documents'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <Icon
                    icon='magnifying-glass'
                    fontSize={16}
                    color={theme.palette.text.secondary}
                  />
                </InputAdornment>
              ),
            },
          }}
        />
        <Select
          size='small'
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          displayEmpty
          renderValue={(val) => {
            if (!val) return 'All tags';
            return availableTags.find((t) => t.id === val)?.name || val;
          }}
        >
          <MenuItem value=''>All tags</MenuItem>
          {availableTags.map((tag) => (
            <MenuItem key={tag.id} value={tag.id}>
              {tag.name}
            </MenuItem>
          ))}
        </Select>
        <Box
          sx={{
            flex: 1,
          }}
        />
        <Button variant='contained' onClick={handleUploadClick}>
          Upload document
        </Button>
      </Stack>

      {isLoading && (
        <Stack
          sx={{
            alignItems: 'center',
            py: 4,
          }}
        >
          <CircularProgress size={32} />
        </Stack>
      )}

      {!isLoading && filteredDocuments.length > 0 && (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            overflow: 'auto',
            flex: 1,
          }}
        >
          {filteredDocuments.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              onDownload={onDownload}
              onDelete={onDelete}
            />
          ))}
        </Box>
      )}

      {!isLoading && filteredDocuments.length === 0 && (
        <Stack
          spacing={2}
          sx={{
            alignItems: 'center',
            py: 6,
          }}
        >
          <Icon
            icon='folder-open'
            fontSize={48}
            color={theme.palette.text.disabled}
          />
          <Typography
            variant='body1'
            sx={{
              color: 'text.secondary',
            }}
          >
            {search || tagFilter
              ? 'No documents match your filters'
              : 'No documents yet'}
          </Typography>
        </Stack>
      )}

      <UploadDialog
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        file={pendingFile}
        availableTags={availableTags}
        isUploading={isUploading}
        onUpload={handleUpload}
      />
    </Stack>
  );
};

AdminDocumentsPanel.displayName = 'AdminDocumentsPanel';
export default AdminDocumentsPanel;
